/**
 * What a counter holds, and how an amount moves (D244) — the small shared
 * pieces the condition evaluator, the effect applier, the stock, the pace
 * and the describer all have to agree about.
 *
 * @remarks
 * A leaf for `item-state.ts`'s reason (D55): "how much air is in the suit"
 * is asked by a condition, a countdown, a stock and a line of prose, and a
 * reading four modules share belongs to none of them.
 *
 * The world holds counters as a DELTA — `world.counterValues` names only
 * what an effect has moved — so every read goes through
 * {@link counterValueOf}, which supplies the declared `initial` for a
 * counter nothing has touched. Every write goes through
 * {@link withCounterValue}, after {@link clampedToCounterRange}: no value
 * outside the declared range is ever stored.
 *
 * @packageDocumentation
 */

import { type CounterDefinition, type CounterId, type RuntimeState, withCounterTokensReplaced } from '@orpheus/content-schema';

import type { IndexedPack } from '../bundle/engine-game-bundle.js';

import { requireCounter } from '../bundle/bundle-lookups.js';

/**
 * What a counter holds right now.
 *
 * @param bundle - The loaded bundle (the counter's declared start)
 * @param state - The live runtime state
 * @param counterId - The counter
 * @returns Its value: the moved one, or the declared `initial` when nothing has moved it
 */
export const counterValueOf = (bundle: IndexedPack, state: RuntimeState, counterId: CounterId): number =>
	state.world.counterValues[counterId] ?? requireCounter(bundle, counterId).initial;

/**
 * An amount, held inside a counter's declared range — the clamp both
 * counter effects and a stock's draw obey.
 *
 * @param counter - The declaration
 * @param value - The amount asked for
 * @returns The amount the counter can hold
 */
export const clampedToCounterRange = (counter: CounterDefinition, value: number): number => Math.min(counter.max, Math.max(counter.min, value));

/**
 * Which edge of its range a move carried a counter ONTO, if either — the
 * crossing `onMin` and `onMax` answer.
 *
 * @remarks
 * A crossing is arriving, never staying: a counter already at its floor and
 * told to go lower has not crossed anything, and neither has one that never
 * moved.
 *
 * @param counter - The declaration
 * @param before - The value before the move
 * @param after - The value after it (already clamped)
 * @returns `'min'` or `'max'` for an arrival at that edge, else `undefined`
 */
export const counterCrossingOf = (counter: CounterDefinition, before: number, after: number): 'max' | 'min' | undefined => {
	if (after === before) {
		return undefined;
	}

	if (after === counter.min) {
		return 'min';
	}

	return after === counter.max ? 'max' : undefined;
};

/**
 * The state with one counter holding a new amount.
 *
 * @param state - The state before
 * @param counterId - The counter
 * @param value - Its new amount, already inside its range
 * @returns The state with the amount written
 */
export const withCounterValue = (state: RuntimeState, counterId: CounterId, value: number): RuntimeState => ({
	...state,
	world: { ...state.world, counterValues: { ...state.world.counterValues, [counterId]: value }},
});

/**
 * D244 (Act II §13.1c): authored prose with every `{counter:<id>}` token
 * replaced by that counter's value now.
 *
 * @remarks
 * The loader refuses a token naming no declared counter, so an unknown name
 * here is a corrupt bundle and throws as every missing definition does
 * (`requireCounter`).
 *
 * @param text - The resolved prose
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The prose with its numbers printed
 */
export const withCountersPrinted = (text: string, bundle: IndexedPack, state: RuntimeState): string =>
	withCounterTokensReplaced(text, (counterId) => String(counterValueOf(bundle, state, counterId as CounterId)));
